import queryString, { StringifyOptions } from 'query-string'

import { DEFAULT_SEARCH_FILTER_KEY } from '@/components/layout-components/CorePageListView'
import { PlatformActionTab } from '@/modules/actions/constants'
import { PermitTab } from '@/modules/permits/constants'
import { SubcontractorsTabKey } from '@/modules/subcontractors/constants'

import {
  ArchivedFilter,
  DefectPriority,
  DefectStatus,
  InductionStatus,
  MsdsReviewDate,
  MsdsStatus,
  PermitActiveStatus,
  PermitStatus,
  PlatformActionPriority,
  PlatformActionStatus,
  SignedOffFilter,
  SortOrder,
  SwmsStatus,
  TicketReviewStatus,
  TimesheetEntryStatus,
} from 'types/api.graphql'

const BASE_QS_OPTIONS: StringifyOptions = {
  arrayFormat: 'bracket',
}

export const stringifyUrl = (url: string, params: Record<string, unknown>) => {
  return queryString.stringifyUrl(
    { url, query: params as NodeJS.Dict<string> },
    BASE_QS_OPTIONS
  )
}

export const stringifyParams = (params: Record<string, unknown>) => {
  return queryString.stringify(params, { arrayFormat: 'bracket' })
}

const TAB_PARAM = 'tab'

export const DEFAULT_PAGINATION_KEYS = {
  pageSize: 'pageSize' as const,
  page: 'page' as const,
}

/**
 * DON'T DIRECTLY IMPORT FROM THIS FILE. USE THE EXPORTED TYPE `Params` FROM `@utils/index`
 */

type DefaultSearch = {
  [DEFAULT_SEARCH_FILTER_KEY]?: string
}

export type CustomPaginationQueryKeys = {
  pagesize: string
  page: string
}

export type Pagination =
  | {
      [DEFAULT_PAGINATION_KEYS.page]: number
      [DEFAULT_PAGINATION_KEYS.pageSize]: number
    }
  | {
      [key in string]: number
    }

export type Sort = {
  sortKey?: string
  direction?: SortOrder
}

export type DateRangeFilter = [string, string] // [afterDateTime, beforeDateTime]

export type IncludeSublocationsFilter = {
  includeSublocations?: boolean
}

export enum PermitListFilter {
  ReviewStatus = 'reviewStatus',
  ActiveStatus = 'activeStatus',
  Archived = 'archived',
  Workers = 'workers',
  DateRange = 'dateRange',
  PermitTypeByIds = 'permitTypeByIds',
  CompanyIds = 'companyIds',
}

export type PermitList = DefaultSearch & {
  [PermitListFilter.ReviewStatus]?: PermitStatus[]
  [PermitListFilter.ActiveStatus]?: PermitActiveStatus[]
  [PermitListFilter.Archived]?: ArchivedFilter
  [PermitListFilter.Workers]?: string[]
  [PermitListFilter.DateRange]?: string[]
  [PermitListFilter.PermitTypeByIds]?: string[]
  [PermitListFilter.CompanyIds]?: string[]
}

export type GalleryList = DefaultSearch & {
  imageSources: string[]
}

export type PermitEdit = {
  [TAB_PARAM]: keyof typeof PermitTab
}

export enum RegisterListFilter {
  DateRange = 'dateRange',
  Archived = 'archived',
}

export type RegistersList = DefaultSearch & {
  [RegisterListFilter.Archived]?: ArchivedFilter
  [RegisterListFilter.DateRange]?: DateRangeFilter
}

export enum RegisterItemsListFilter {
  Projects = 'projects',
  Archived = 'archived',
  Workers = 'workers',
  Subcontractors = 'subcontractors',
  AdminUsers = 'adminUsers',
}

export type RegisterItemsList = DefaultSearch & {
  [RegisterItemsListFilter.Projects]?: string[]
  [RegisterItemsListFilter.Archived]?: ArchivedFilter
  [RegisterItemsListFilter.Workers]?: string[]
  [RegisterItemsListFilter.Subcontractors]?: string[]
  [RegisterItemsListFilter.AdminUsers]?: string[]
}

export enum SwmsLibraryListFilter {
  Search = 'librarySearch',
  Status = 'libraryStatus',
  Subcontractors = 'librarySubcontractors',
  Expired = 'libraryExpired',
  Archived = 'libraryArchived',
}

export type SwmsLibraryList = {
  [SwmsLibraryListFilter.Search]?: string
  [SwmsLibraryListFilter.Status]?: SwmsStatus[]
  [SwmsLibraryListFilter.Subcontractors]?: string[]
  [SwmsLibraryListFilter.Expired]?: boolean
  [SwmsLibraryListFilter.Archived]?: ArchivedFilter
}

export enum SwmsListFilter {
  CreatedAt = 'createdAt',
  Subcontractors = 'subcontractors',
  Archived = 'archived',
  Status = 'status',
}

export type SwmsList = DefaultSearch & {
  [SwmsListFilter.CreatedAt]?: DateRangeFilter
  [SwmsListFilter.Subcontractors]?: string[]
  [SwmsListFilter.Archived]?: ArchivedFilter
  [SwmsListFilter.Status]?: SwmsStatus[]
}

export enum SwmsBuilderQueryActions {
  Edit = 'edit',
}

export type SwmsBuilderQueryActionsType = {
  [SwmsBuilderQueryActions.Edit]?: boolean
}

export enum SdsListFilter {
  SdsReviewStatus = 'sdsReviewStatus',
  SdsReviewDate = 'sdsReviewDate',
  Archived = 'archived',
  CreatedAt = 'createdBetween',
}

export type SdsList = DefaultSearch & {
  [SdsListFilter.SdsReviewStatus]?: MsdsStatus[]
  [SdsListFilter.SdsReviewDate]?: MsdsReviewDate[]
  [SdsListFilter.Archived]?: ArchivedFilter
  [SdsListFilter.CreatedAt]?: DateRangeFilter
}

export enum ActionsListFilter {
  AdminUsers = 'adminUsers',
  Subcontractors = 'subcontractors',
  Workers = 'workers',
  Status = 'status',
  PriorityType = 'priorityType',
  Type = 'type',
  DueDate = 'dueDate',
  Archived = 'archived',
  Location = 'location',
}

export type ActionsList = DefaultSearch &
  IncludeSublocationsFilter & {
    [TAB_PARAM]: keyof typeof PlatformActionTab
    [ActionsListFilter.AdminUsers]?: string[]
    [ActionsListFilter.Subcontractors]?: string[]
    [ActionsListFilter.Workers]?: string[]
    [ActionsListFilter.Status]?: PlatformActionStatus[]
    [ActionsListFilter.PriorityType]?: PlatformActionPriority[]
    [ActionsListFilter.Type]?: string[]
    [ActionsListFilter.DueDate]?: DateRangeFilter
    [ActionsListFilter.Archived]?: ArchivedFilter
    [ActionsListFilter.Location]?: string
  }

export enum SiteInductionListFilter {
  CreatedAt = 'createdAt',
  Archived = 'archived',
  Workers = 'workers',
  Subcontractors = 'subcontractors',
  Status = 'status',
}

export type SiteInductionsList = DefaultSearch & {
  [SiteInductionListFilter.CreatedAt]?: DateRangeFilter
  [SiteInductionListFilter.Archived]?: ArchivedFilter
  [SiteInductionListFilter.Workers]?: string[]
  [SiteInductionListFilter.Subcontractors]?: string[]
  [SiteInductionListFilter.Status]?: InductionStatus[]
}

export enum CompanyInductionListFilter {
  CreatedAt = 'createdAt',
  Archived = 'archived',
  Workers = 'workers',
  Subcontractors = 'subcontractors',
  Status = 'status',
}

export type CompanyInductionsList = DefaultSearch & {
  [CompanyInductionListFilter.CreatedAt]?: DateRangeFilter
  [CompanyInductionListFilter.Archived]?: ArchivedFilter
  [CompanyInductionListFilter.Workers]?: string[]
  [CompanyInductionListFilter.Subcontractors]?: string[]
  [CompanyInductionListFilter.Status]?: InductionStatus[]
}

export enum TimesheetsListFilter {
  CreatedAt = 'createdAt',
  Status = 'status',
  TimeType = 'timeType',
  Workers = 'workers',
  Subcontractors = 'subcontractors',
}

export type TimesheetsList = DefaultSearch & {
  [TimesheetsListFilter.CreatedAt]?: DateRangeFilter
  [TimesheetsListFilter.Status]?: TimesheetEntryStatus[]
  [TimesheetsListFilter.TimeType]?: string[]
  [TimesheetsListFilter.Workers]?: string[]
  [TimesheetsListFilter.Subcontractors]?: string[]
}

export enum TicketListFilter {
  ReviewStatus = 'reviewStatus',
  ExpiryStatus = 'expiryStatus',
  HideManual = 'hideManual',
  HideStaff = 'hideStaff',
  Workers = 'workers',
  Subcontractors = 'subcontractors',
  Tickets = 'tickets',
}

export type TicketList = DefaultSearch & {
  [TicketListFilter.ReviewStatus]?: TicketReviewStatus[]
  [TicketListFilter.ExpiryStatus]?: string[]
  [TicketListFilter.HideManual]?: boolean
  [TicketListFilter.HideStaff]?: boolean
  [TicketListFilter.Workers]?: string[]
  [TicketListFilter.Subcontractors]?: string[]
  [TicketListFilter.Tickets]?: string[]
}

export enum SiteDiaryListFilter {
  Archived = 'archived',
}

export type SiteDiaryList = {
  [SiteDiaryListFilter.Archived]?: ArchivedFilter
}

export enum DefectListFilter {
  Status = 'status',
  Priority = 'priority',
  CreatedBy = 'createdBy',
  Subcontractors = 'subcontractors',
  AdminUsers = 'adminUsers',
  Workers = 'workers',
  Overdue = 'overdue',
  Pending = 'pending',
  Type = 'type',
  CreatedAt = 'createdAt',
  Location = 'location',
}

export type DefectList = DefaultSearch &
  IncludeSublocationsFilter & {
    showNewModal?: boolean
    [DefectListFilter.Status]?: DefectStatus[]
    [DefectListFilter.Priority]?: DefectPriority[]
    [DefectListFilter.CreatedBy]?: string[]
    [DefectListFilter.Subcontractors]?: string[]
    [DefectListFilter.AdminUsers]?: string[]
    [DefectListFilter.Workers]?: string[]
    [DefectListFilter.Overdue]?: boolean
    [DefectListFilter.Pending]?: boolean
    [DefectListFilter.Type]?: string[]
    [DefectListFilter.CreatedAt]?: DateRangeFilter
    [DefectListFilter.Location]?: string
  }

export enum SubbieListFilter {
  Status = 'status',
  ResourceTags = 'resourceTags',
  NoSwms = 'noSwms',
}

export type SubbieList = DefaultSearch & {
  [TAB_PARAM]?: SubcontractorsTabKey
  [SubbieListFilter.Status]?: string[]
  [SubbieListFilter.ResourceTags]?: string[]
  [SubbieListFilter.NoSwms]?: boolean
}

export enum SignOnListFilter {
  CreatedAt = 'createdAt',
  VisitorsOnly = 'visitorsOnly',
  Workers = 'workers',
  Companies = 'companies',
  Archived = 'archived',
  SignedOff = 'signedOffFilter',
  GateAccessOnly = 'gateAccessOnly',
}

export type SignOnList = DefaultSearch & {
  [SignOnListFilter.VisitorsOnly]?: boolean
  [SignOnListFilter.Workers]?: string[]
  [SignOnListFilter.Companies]?: string[]
  [SignOnListFilter.Archived]?: ArchivedFilter
  [SignOnListFilter.SignedOff]?: SignedOffFilter
  [SignOnListFilter.GateAccessOnly]?: boolean
  [SignOnListFilter.CreatedAt]?: DateRangeFilter
}

export enum WorkerListFilter {
  HideManual = 'hideManual',
  HideBuilderSubbie = 'hideBuilderSubbie',
  Archived = 'archived',
  MissingWhiteCard = 'missingWhiteCard',
  TicketReviewStatus = 'ticketReviewStatus',
  Trade = 'trade',
  EmploymentStatus = 'employmentStatus',
  Subcontractors = 'subcontractors',
}

export type WorkerList = DefaultSearch & {
  [WorkerListFilter.HideManual]?: boolean
  [WorkerListFilter.HideBuilderSubbie]?: boolean
  [WorkerListFilter.Archived]?: boolean
  [WorkerListFilter.MissingWhiteCard]?: boolean
  [WorkerListFilter.TicketReviewStatus]?: string[]
  [WorkerListFilter.Trade]?: string[]
  [WorkerListFilter.EmploymentStatus]?: string[]
  [WorkerListFilter.Subcontractors]?: string[]
}

export * as Params from './params'
